import {
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  ScreenWrap,
  HeaderBreadcrumbs,
  SoloLoading,
  ScreenError,
} from '../../components';
import AssignedUserRow from './AssignedUserRow';
import ReassignCoachModal from '../../components/modals/ReassignCoachModal';
import { useParams } from 'react-router';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { Api } from '../../api';
import { useApiRead } from 'api-read-hook';
import { HydraCollection } from '../../types';
import { useState } from 'react';

export default function ClinicianAssignedUsers() {
  const { id } = useParams();
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<Api.User.V_ClientList | null>(null);

  const { tablePaginationProps, tableSortLabelProps } = useModifiableParams();

  const api = useApiRead<HydraCollection<Api.User.V_ClientList>>(
    id ? `/clinicians/${id}/clients` : null,
  );
  const clinicianApi = useApiRead<Api.User.V_ClinicianDetailAsAdmin>(
    id ? `/clinicians/${id}` : null,
  );

  const isFounder = clinicianApi.data?.clinicianProfile?.type === 'founder';
  const clinicianType = clinicianApi.data?.clinicianProfile?.type;

  const handleReassignClick = (user: Api.User.V_ClientList) => {
    setSelectedUser(user);
    setReassignModalOpen(true);
  };

  if (api.error) return <ScreenError {...api} />;

  const assignedUsers = api.data?.member;
  const total = api.data?.totalItems;

  return (
    <ScreenWrap>
      <Stack spacing={3} alignItems="stretch">
        <Stack gap={1}>
          <HeaderBreadcrumbs
            title={`Assigned Users`}
            items={[{ name: 'Clinicians', url: '/clinicians' }]}
          />
        </Stack>

        <Stack spacing={2}>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel {...tableSortLabelProps('name')}>
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Program Status</TableCell>
                    <TableCell>Appointments</TableCell>
                    <TableCell>Registration Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedUsers ? (
                    assignedUsers.length > 0 ? (
                      assignedUsers.map((client) => (
                        <AssignedUserRow
                          key={client['@id']}
                          user={client}
                          onReassignClick={() => handleReassignClick(client)}
                          hideReassign={isFounder}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No users assigned to this clinician
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <SoloLoading />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter
                  sx={{
                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  <TableRow>
                    <TablePagination
                      {...tablePaginationProps({
                        totalItems: total || 0,
                        rowsPerPage: 10,
                      })}
                      count={total || 0}
                      colSpan={6}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      </Stack>

      <ReassignCoachModal
        open={reassignModalOpen}
        onClose={() => {
          setReassignModalOpen(false);
          setSelectedUser(null);
        }}
        userName={selectedUser?.user?.name || ''}
        currentClinicianId={id}
        targetType={clinicianType === 'physician' ? 'physician' : 'coach'}
        userId={selectedUser?.user?.id}
      />
    </ScreenWrap>
  );
}
